import { useState, useEffect, useRef } from 'react'
import { Card, Row, Col, Statistic, Button, Space, Typography, Spin, message, Tag, Divider, Switch, Tooltip, Progress } from 'antd'
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  MailOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined
} from '@ant-design/icons'
import transactionService from '../services/transactionService'
import gmailService from '../services/gmailService'
import settingsService from '../services/settingsService'
import skuService from '../services/skuService'
import dayjs from 'dayjs'

const { Title } = Typography

function DashboardPage() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [polling, setPolling] = useState(false)
  const [pollingStatus, setPollingStatus] = useState(null)
  const [schedulerStatus, setSchedulerStatus] = useState(null)
  const [pollingStatusLoading, setPollingStatusLoading] = useState(false)
  const [useAiV2, setUseAiV2] = useState(false)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [skuSync, setSkuSync] = useState(null)
  const [skuSyncing, setSkuSyncing] = useState(false)
  const skuPollRef = useRef(null)

  useEffect(() => {
    fetchStats()
    fetchPollingStatuses()
    fetchSettings()
    fetchSkuStatus()
    return () => {
      if (skuPollRef.current) clearInterval(skuPollRef.current)
    }
  }, [])

  const fetchSkuStatus = async () => {
    try {
      const data = await skuService.getStatus()
      setSkuSync(data)
      if (data?.running) startSkuPolling()
    } catch (error) {
      // non-fatal: the card just won't show a last-sync time
    }
  }

  const startSkuPolling = () => {
    if (skuPollRef.current) return
    setSkuSyncing(true)
    skuPollRef.current = setInterval(async () => {
      try {
        const data = await skuService.getStatus()
        setSkuSync(data)
        if (!data?.running) {
          clearInterval(skuPollRef.current)
          skuPollRef.current = null
          setSkuSyncing(false)
          if (data?.error) {
            message.error(`SKU refresh failed: ${data.error}`)
          } else {
            const r = data?.lastResult
            message.success(
              r
                ? `SKU data refreshed: ${r.customers} customers, ${r.item_customers} item assignments, ${r.prices} prices`
                : 'SKU data refreshed'
            )
          }
        }
      } catch (error) {
        // keep polling; transient errors are expected around a restart
      }
    }, 1000)
  }

  const handleSkuRefresh = async () => {
    setSkuSyncing(true)
    try {
      await skuService.sync()
      // Seed an immediate 0% state so the bar appears without waiting for the first poll.
      setSkuSync((prev) => ({ ...(prev || {}), running: true, pct: 0, phase: 'Starting', detail: '', error: '' }))
      startSkuPolling()
    } catch (error) {
      setSkuSyncing(false)
      message.error(`Failed to start SKU refresh: ${error.response?.data?.error || error.message}`)
    }
  }
  const fetchSettings = async () => {
    setSettingsLoading(true)
    try {
      const data = await settingsService.get()
      setUseAiV2(!!data.useAiExtractionV2)
    } catch (error) {
      message.error('Failed to load settings')
    } finally {
      setSettingsLoading(false)
    }
  }

  const handleToggleAiV2 = async (checked) => {
    setSettingsLoading(true)
    try {
      const data = await settingsService.update({ useAiExtractionV2: !!checked })
      setUseAiV2(!!data.useAiExtractionV2)
      message.success(`AI Extraction set to ${data.useAiExtractionV2 ? 'V2' : 'V1'}`)
    } catch (error) {
      message.error(`Failed to update setting: ${error.response?.data?.error || error.message}`)
    } finally {
      setSettingsLoading(false)
    }
  }

  const fetchPollingStatuses = async () => {
    setPollingStatusLoading(true)
    try {
      const [remote, scheduler] = await Promise.all([
        gmailService.getStatus(),
        gmailService.getSchedulerStatus()
      ])
      setPollingStatus(remote)
      setSchedulerStatus(scheduler)
    } catch (error) {
      message.error('Failed to load polling status')
    } finally {
      setPollingStatusLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const data = await transactionService.getStats()
      setStats(data)
    } catch (error) {
      message.error('Failed to load statistics')
    } finally {
      setLoading(false)
    }
  }

  const handleStartProcessing = async () => {
    setPolling(true)
    try {
      // Step 1: Check polling status
      message.loading('Checking Gmail polling status...', 0)
      const status = await gmailService.getStatus()

      // Step 2: Start polling if not active
      if (!status.active) {
        message.destroy()
        message.loading('Starting Gmail polling service...', 0)
        await gmailService.startPolling()
        message.destroy()
        message.success('Gmail polling started (checks every 2 hours)')

        // Wait a bit for polling to collect emails
        message.loading('Waiting for initial email collection...', 0)
        await new Promise(resolve => setTimeout(resolve, 5000))
      }
      message.destroy()

      // Step 3: Process emails from our side
      // IMPORTANT: Use async endpoint so the UI doesn't time out (PDF processing can take >30s).
      const msgKey = 'polling-async'
      message.loading({ content: 'Processing emails with PDF attachments...', key: msgKey, duration: 0 })
      await gmailService.pollAsync()

      // Poll async status until finished (or timeout)
      const startedAt = Date.now()
      const maxWaitMs = 10 * 60 * 1000 // 10 minutes
      while (true) {
        await new Promise((r) => setTimeout(r, 2000))
        const st = await gmailService.getPollAsyncStatus()

        if (!st?.running) {
          message.destroy(msgKey)
          const found = st?.emailsFound ?? 0
          const processed = st?.emailsProcessed ?? 0
          const skipped = st?.emailsSkipped ?? 0
          const failed = st?.emailsFailed ?? 0

          if (st?.error) {
            message.error(`Processing finished with error: ${st.error}`)
          } else if (found === 0) {
            message.info('No new emails with PO attachments found')
          } else {
            message.success(`✅ Processing complete! Found: ${found}, Processed: ${processed}, Skipped: ${skipped}, Failed: ${failed}`)
          }
          break
        }

        if (Date.now() - startedAt > maxWaitMs) {
          message.destroy(msgKey)
          message.warning('Processing is still running in the background. Refresh Transactions in a minute.')
          break
        }
      }

      await fetchStats()
      await fetchPollingStatuses()
    } catch (error) {
      message.destroy()
      message.error(`Failed: ${error.response?.data?.error || error.message}`)
    } finally {
      setPolling(false)
    }
  }

  const handleStartScheduler = async () => {
    setPollingStatusLoading(true)
    try {
      await gmailService.startScheduler()
      message.success('Polling scheduler started (every 2 minutes)')
      await fetchPollingStatuses()
    } catch (error) {
      message.error(`Failed to start scheduler: ${error.response?.data?.error || error.message}`)
    } finally {
      setPollingStatusLoading(false)
    }
  }

  const handleStopScheduler = async () => {
    setPollingStatusLoading(true)
    try {
      await gmailService.stopScheduler()
      message.success('Polling scheduler stopped')
      await fetchPollingStatuses()
    } catch (error) {
      message.error(`Failed to stop scheduler: ${error.response?.data?.error || error.message}`)
    } finally {
      setPollingStatusLoading(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    )
  }

  const successRate = stats?.total > 0
    ? ((stats.success / stats.total) * 100).toFixed(1)
    : 0

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Dashboard
          </Title>
          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={handleStartProcessing}
            loading={polling}
            size="large"
            block={window.innerWidth < 576}
          >
            Start Processing Emails
          </Button>
        </div>

        <Card>
          <Row align="middle" gutter={[16, 16]}>
            <Col xs={24} md={14}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space>
                  <DatabaseOutlined style={{ fontSize: 22 }} />
                  <Title level={3} style={{ margin: 0 }}>
                    SKU Database
                  </Title>
                </Space>
                <div style={{ opacity: 0.75 }}>
                  Pull the latest customers, item assignments and prices from the SKU DB into the integration.
                </div>
                <div style={{ opacity: 0.75 }}>
                  Last synced:{' '}
                  <strong>
                    {skuSync?.metadata?.last_sync_at
                      ? dayjs(skuSync.metadata.last_sync_at).format('YYYY-MM-DD HH:mm:ss')
                      : 'never'}
                  </strong>
                  {skuSync?.metadata
                    ? ` - ${skuSync.metadata.customers} customers, ${skuSync.metadata.item_customers} item assignments, ${skuSync.metadata.prices} prices`
                    : ''}
                </div>
              </Space>
            </Col>
            <Col xs={24} md={10} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                size="large"
                icon={<CloudDownloadOutlined />}
                onClick={handleSkuRefresh}
                loading={skuSyncing}
                style={{ height: 56, fontSize: 18, paddingInline: 28, fontWeight: 600 }}
                block={window.innerWidth < 768}
              >
                {skuSyncing ? 'Refreshing...' : 'Refresh from SKU DB'}
              </Button>
            </Col>
          </Row>

          {(skuSyncing || (skuSync?.pct > 0 && skuSync?.pct < 100)) && (
            <div style={{ marginTop: 20 }}>
              <Progress
                percent={skuSync?.pct ?? 0}
                status={skuSync?.error ? 'exception' : skuSync?.running ? 'active' : 'success'}
                strokeWidth={14}
              />
              <div style={{ marginTop: 8, opacity: 0.85 }}>
                <strong>{skuSync?.phase || 'Starting'}</strong>
                {skuSync?.detail ? ` - ${skuSync.detail}` : ''}
              </div>
            </div>
          )}
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="AI Extraction Settings">
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Space wrap>
                    <strong>AI Extraction Version:</strong>
                    <Tag color={useAiV2 ? 'blue' : 'default'}>
                      {useAiV2 ? 'V2' : 'V1'}
                    </Tag>
                  </Space>
                  <Tooltip title="Runtime toggle. After a backend/container restart, it will revert to the env var value unless you also change USE_AI_EXTRACTION_V2.">
                    <Switch
                      checked={useAiV2}
                      onChange={handleToggleAiV2}
                      loading={settingsLoading}
                      checkedChildren="V2"
                      unCheckedChildren="V1"
                    />
                  </Tooltip>
                </Space>
                <div style={{ opacity: 0.8 }}>
                  Current selection is applied to the next email processing run.
                </div>
                <Button onClick={fetchSettings} loading={settingsLoading}>
                  Refresh Setting
                </Button>
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="Polling Status"
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchPollingStatuses}
                  loading={pollingStatusLoading}
                >
                  Refresh
                </Button>
              }
            >
              <Spin spinning={pollingStatusLoading}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div>
                    <Space wrap>
                      <strong>Local Scheduler:</strong>
                      {schedulerStatus?.running ? (
                        <Tag color="green">Running</Tag>
                      ) : (
                        <Tag color="red">Stopped</Tag>
                      )}
                      <span style={{ opacity: 0.8 }}>
                        Interval: {schedulerStatus?.interval_seconds ?? 'N/A'}s
                      </span>
                    </Space>
                  </div>

                  <div style={{ opacity: 0.8 }}>
                    Next run:{' '}
                    {schedulerStatus?.next_run
                      ? dayjs(schedulerStatus.next_run).format('YYYY-MM-DD HH:mm:ss')
                      : 'N/A'}
                  </div>

                  <Space wrap>
                    <Button
                      type="primary"
                      icon={<PlayCircleOutlined />}
                      onClick={handleStartScheduler}
                      disabled={!!schedulerStatus?.running}
                      loading={pollingStatusLoading}
                    >
                      Enable Polling
                    </Button>
                    <Button
                      danger
                      icon={<PauseCircleOutlined />}
                      onClick={handleStopScheduler}
                      disabled={!schedulerStatus?.running}
                      loading={pollingStatusLoading}
                    >
                      Disable Polling
                    </Button>
                  </Space>

                  <Divider style={{ margin: '12px 0' }} />

                  <div>
                    <Space wrap>
                      <strong>Remote Gmail Polling:</strong>
                      {pollingStatus?.active ? (
                        <Tag color="green">Active</Tag>
                      ) : (
                        <Tag color="red">Inactive</Tag>
                      )}
                      <span style={{ opacity: 0.8 }}>
                        Interval: {pollingStatus?.interval ?? 'N/A'}s
                      </span>
                    </Space>
                  </div>
                  <div style={{ opacity: 0.8 }}>Email: {pollingStatus?.email || 'N/A'}</div>
                </Space>
              </Spin>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Transactions"
                value={stats?.total || 0}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Successful"
                value={stats?.success || 0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Failed"
                value={stats?.failed || 0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Success Rate"
                value={successRate}
                suffix="%"
                valueStyle={{ color: successRate > 90 ? '#3f8600' : '#fa8c16' }}
                prefix={<SyncOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Card title="Quick Actions">
          <Space size="middle" wrap>
            {/* Stage 2: Customers and Item Mappings are managed in the SKU DB; links hidden. */}
            <Button type="primary" href="/transactions">View Transaction Logs</Button>
          </Space>
        </Card>
      </Space>
    </div>
  )
}

export default DashboardPage
